import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Image from "next/image";
import { Package } from "lucide-react";
import { useAuthStore } from "@/stores/auth.store";
import { useToast } from "@/stores/ui.store";
import { authFetch } from "@/lib/auth-client";
import { refreshBalance } from "@/lib/balance";
import { Button } from "@/components/ui/button";
import { asset } from "@/lib/asset";
import { PackGrid, type StorePack } from "@/components/store/PackGrid";
import { PurchaseModal } from "@/components/store/PurchaseModal";
import { PACKS_COLLECTION_LIVE } from "@/config/features";
import { COIN_BUNDLES } from "@/config/coin-bundles";
import {
  purchaseBundle,
  PurchaseCancelledError,
} from "@/services/payments/jest-payments";

import { Tabs } from "@/components/ui/tabs";
import { CoinGlyph } from "@/components/ui/coin";

type Tab = "coins" | "packs";

export default function StorePage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const initialTab: Tab =
    PACKS_COLLECTION_LIVE && searchParams.get("tab") === "packs" ? "packs" : "coins";

  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [selectedPack, setSelectedPack] = useState<StorePack | null>(null);
  const { isAuthenticated, accessToken, user, updateBalance } = useAuthStore();
  const { success: showSuccess, error: showError } = useToast();

  const selectTab = (tab: Tab) => {
    setActiveTab(tab);
    router.replace(tab === "packs" ? "/store?tab=packs" : "/store", {
      scroll: false,
    });
  };

  const handleBuyCoins = async (bundleId: string) => {
    if (!isAuthenticated || !accessToken) {
      showError("Sign In Required", "Please sign in to purchase coins");
      return;
    }
    if (typeof window.JestSDK === "undefined") {
      showError(
        "Payments Unavailable",
        "Jest payments are not loaded in this environment",
      );
      return;
    }

    try {
      setLoadingId(bundleId);
      const { coinsAdded } = await purchaseBundle(
        window.JestSDK,
        bundleId,
        accessToken,
      );

      if (user && coinsAdded > 0) {
        updateBalance({ coins: user.coins + coinsAdded });
      }
      showSuccess(
        "Purchase Complete",
        coinsAdded > 0
          ? `${coinsAdded.toLocaleString()} coins added to your balance.`
          : "Coins have been added to your balance.",
      );
    } catch (err) {
      if (err instanceof PurchaseCancelledError) {
        // Player dismissed Jest's checkout modal — silent no-op.
        return;
      }
      showError(
        "Purchase Failed",
        err instanceof Error ? err.message : "Something went wrong",
      );
    } finally {
      setLoadingId(null);
    }
  };

  const handlePackSelect = (pack: StorePack) => {
    if (!isAuthenticated) {
      showError("Sign In Required", "Please sign in to purchase packs");
      return;
    }
    setSelectedPack(pack);
  };

  // One POST per pack (the API is single-pack); sequential so a mid-run
  // failure surfaces with the packs already bought still credited.
  const handleConfirmPurchase = async (
    paymentMethod: "USD" | "COINS",
    quantity: number,
  ) => {
    if (!selectedPack) return;
    for (let i = 0; i < quantity; i++) {
      const res = await authFetch("/api/packs/purchase", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ packTypeId: selectedPack.id, paymentMethod }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data.code === "INSUFFICIENT_BALANCE"
            ? `Insufficient balance${i > 0 ? ` — ${i} pack${i > 1 ? "s" : ""} purchased` : ""}`
            : data.error || "Purchase failed",
        );
      }
    }
    void refreshBalance();
  };

  return (
    <div className="min-h-screen bg-surface">
      <div className="ornamental-rule max-w-7xl mx-auto px-4 pt-6 sm:pt-8" />

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        <div className="mb-5 sm:mb-6">
          <span className="text-gold text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase mb-3 sm:mb-4 block">
            The Mint
          </span>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold text-cream mb-2">
            {activeTab === "packs" ? "Card Packs" : "Buy Coins"}
          </h1>
          <p className="text-sm sm:text-base text-warm-gray">
            {activeTab === "packs"
              ? "Pull NFL player cards and build your Best Ball roster."
              : "Purchase coins to enter events, buy props, and boost your picks."}
          </p>
        </div>

        {/* Coins | Packs tabs — hidden entirely while packs aren't live */}
        {PACKS_COLLECTION_LIVE && (
        <Tabs
          label="Store sections"
          value={activeTab}
          onValueChange={selectTab}
          className="mb-6 sm:mb-8"
          tabs={[
            { id: "coins", label: "Coins", icon: <CoinGlyph size="lg" /> },
            { id: "packs", label: "Packs", icon: <Package className="h-5 w-5" /> },
          ]}
        />
        )}

        {activeTab === "coins" && (
          <>
            {/* Coin Bundles — 2-up until there is room for the full ladder on
                one row, so the four tiers never break to a lone orphan. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {COIN_BUNDLES.map((bundle) => (
                <div
                  key={bundle.id}
                  className="relative p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-gold/10 to-gold/5 border border-gold/30 hover:border-gold/50 transition-colors"
                >
                  {bundle.bonus && (
                    <span className="absolute -top-2 -right-2 px-2.5 py-1 rounded-full bg-verdigris text-cream text-xs font-bold">
                      +{bundle.bonus}% BONUS
                    </span>
                  )}
                  <div className="flex items-center gap-3 mb-5 sm:mb-6">
                    <Image
                      src={asset("assets/icons/coins.webp")}
                      alt="Coins"
                      width={48}
                      height={48}
                      unoptimized
                      className="w-11 h-11 sm:w-12 sm:h-12 object-contain"
                    />
                    <div>
                      <p className="text-2xl sm:text-3xl font-bold text-cream">
                        {bundle.coins.toLocaleString()}
                      </p>
                      <p className="text-sm text-warm-gray">Coins</p>
                    </div>
                  </div>
                  <Button
                    className="w-full"
                    variant="coins"
                    size="lg"
                    disabled={loadingId !== null}
                    onClick={() => handleBuyCoins(bundle.id)}
                  >
                    {loadingId === bundle.id ? "Processing..." : bundle.displayPrice}
                  </Button>
                </div>
              ))}
            </div>

            {/* Info note */}
            <div className="mt-6 sm:mt-8 p-4 rounded-xl bg-surface-2 border border-surface-3 text-xs sm:text-sm text-warm-gray">
              <p>
                Payments are processed securely via Jest. Coins are credited
                instantly after successful payment.
              </p>
            </div>
          </>
        )}

        {activeTab === "packs" && (
          <PackGrid
            sport="NFL"
            onPurchase={handlePackSelect}
            loadingPackId={null}
          />
        )}
      </div>

      {/* Pack purchase modal — coins native, USD equivalent */}
      {selectedPack && (
        <PurchaseModal
          isOpen={!!selectedPack}
          onClose={() => setSelectedPack(null)}
          packId={selectedPack.id}
          packName={selectedPack.name}
          price={selectedPack.price}
          priceType={selectedPack.priceType}
          coinPrice={selectedPack.coinPrice}
          usdPrice={selectedPack.usdPrice}
          userCoins={user?.coins ?? 0}
          userGems={user?.gems ?? 0}
          userUsdCents={user?.usdBalanceCents ?? 0}
          onConfirm={handleConfirmPurchase}
        />
      )}
    </div>
  );
}
